import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import api from "../../axiosInstance";
import { toast } from "react-toastify";

const ACCENT = "#17479e";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`;

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTE_STEPS = Array.from({ length: 12 }, (_, i) => i * 5);

const formatSlot = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })} · ${d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}`;
};

const slotToDateInput = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toISOString().split("T")[0] : "";
};

const to12Hour = (h24, m) => {
  const meridiem = h24 >= 12 ? "PM" : "AM";
  let hour12 = h24 % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour12, minute: m, meridiem };
};

const slotToTime12 = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime()))
    return { hour12: 9, minute: 0, meridiem: "AM" };
  return to12Hour(d.getHours(), d.getMinutes());
};

const to24HourString = (hour12, minute, meridiem) => {
  let h = hour12 % 12;
  if (meridiem === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

function MentorRescheduleDialog({ open, booking, onClose, onSuccess }) {
  const [date, setDate] = useState("");
  const [hour12, setHour12] = useState(9);
  const [minute, setMinute] = useState(0);
  const [meridiem, setMeridiem] = useState("AM");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  console.log(booking);

  useEffect(() => {
    if (open && booking) {
      setDate(slotToDateInput(booking.slotStartTs));
      const t = slotToTime12(booking.slotStartTs);
      setHour12(t.hour12);
      setMinute(t.minute);
      setMeridiem(t.meridiem);
      setReason("");
    }
  }, [open, booking]);

  const minuteOptions = MINUTE_STEPS.includes(minute)
    ? MINUTE_STEPS
    : [...MINUTE_STEPS, minute].sort((a, b) => a - b);

  const handleSubmit = async () => {
    if (!booking || !date) return;
    setSaving(true);
    try {
      await api.put(`/admin/mentors/bookings/reschedule/${booking.bookingId}`, {
        date,
        time: to24HourString(hour12, minute, meridiem),
        reason,
      });
      toast.success("Session rescheduled successfully!");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.errorMessage ||
          "Failed to reschedule session. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          background: HEADER_GRADIENT,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              background: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EventRepeatIcon sx={{ color: "#fff", fontSize: 18 }} />
          </Box>
          <Typography
            sx={{ color: "#fff", fontWeight: 700, fontSize: "1.05rem" }}
          >
            Reschedule Session
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ color: "#fff", fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {booking && (
          <Box
            sx={{
              background: "#f5f7fb",
              borderRadius: "10px",
              px: 2,
              py: 1.2,
              mb: 2.5,
              textAlign: "left",
            }}
          >
            <Typography
              sx={{ fontSize: "0.7rem", color: "#8a94a6", fontWeight: 600 }}
            >
              CURRENT SCHEDULE
            </Typography>
            <Typography
              sx={{ fontSize: "0.9rem", fontWeight: 700, color: ACCENT }}
            >
              {formatSlot(booking.slotStartTs)}
            </Typography>
          </Box>
        )}

        <TextField
          fullWidth
          size="small"
          label="New Date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2.5 }}
        />

        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "#37474f",
            mb: 1,
            textAlign: "left",
          }}
        >
          New Time
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Hour</InputLabel>
            <Select
              label="Hour"
              value={hour12}
              onChange={(e) => setHour12(e.target.value)}
            >
              {HOURS_12.map((h) => (
                <MenuItem key={h} value={h}>
                  {String(h).padStart(2, "0")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Minute</InputLabel>
            <Select
              label="Minute"
              value={minute}
              onChange={(e) => setMinute(e.target.value)}
            >
              {minuteOptions.map((m) => (
                <MenuItem key={m} value={m}>
                  {String(m).padStart(2, "0")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>AM/PM</InputLabel>
            <Select
              label="AM/PM"
              value={meridiem}
              onChange={(e) => setMeridiem(e.target.value)}
            >
              <MenuItem value="AM">AM</MenuItem>
              <MenuItem value="PM">PM</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <TextField
          fullWidth
          size="small"
          label="Reason"
          placeholder="Enter reason for rescheduling"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            color: "#616161",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving || !date}
          onClick={handleSubmit}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            background: HEADER_GRADIENT,
            boxShadow: "none",
          }}
        >
          {saving ? "Rescheduling..." : "Reschedule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MentorRescheduleDialog;
